import { Entity } from '@core/api/entity';
import { ArraySerializer, Field, Model, Name, PrimitiveSerializer, Type } from '@junte/serialize-ts';
import { LabelledEntity } from '@core/models/labelled-entity.model';
import { Partner, PartnerSerializer } from './partner.model';
import { FileObject } from './file-object.model';
import { ValueObject } from './value-object.model';
export type UserStatus =
  | 'needs-verification'
  | 'pending'
  | 'problem'
  | 'unverified'
  | 'verification-requested'
  | 'verified';
export type Theme = 'capaccess' | 'ready4impact';

@Model()
export class User extends Entity {
  @Field() public image?: FileObject;
  @Field() public email: string;
  @Field() public firstName: string;
  @Field() public isCapaccessUser: boolean;
  @Field() public isStaff: boolean;
  @Field() public lastName: string;
  @Field() public mobile: string;
  @Field() public phone: string;
  @Field() public salutation: ValueObject;

  @Field() public status: UserStatus;
  @Field() public theme?: Theme;
  @Field() public title?: ValueObject;
  @Field() public isClient: boolean;
  @Field() public isAdvisor: boolean;
  @Field() public partnerAdmin?: LabelledEntity;
  @Field() public isAnalystUser?: boolean;
  @Field()
  @Type(new PartnerSerializer())
  public ddPartner?: Partner;
  @Field() public isRestrictedUser: boolean;
  @Field() public isActive: boolean;
  @Field() public isSuperUser: boolean;
  @Field() public isReadonly: boolean;

  @Field() public isTwoFaEnabled: boolean;
  @Field() public isSmsOtpEnabled: boolean;

  @Field()
  @Name('user_type')
  public userType?: string;

  @Field() isIdentificationComplete: boolean;
  @Field() isOnboardingComplete: boolean;

  @Field()
  @Type(new PrimitiveSerializer())
  public advisedClients?: string[];

  @Field()
  @Name('partnerAdminPartners')
@Type(new ArraySerializer(new PartnerSerializer()))
  public partnerAdminPartners?: Partner[];


  @Field()
  @Type(new PrimitiveSerializer())
  public client?: {
    uid: string;
    clientClass: ValueObject;
    type: string;
    investorClientAdviceLicence: boolean;
    advisorUser: {
      name: string;
      uid: string;
    };
  };

  public static create(data: Partial<User>): User {
    return Object.assign(new User(), data);
  }

  public getFullName(): string {
    const parts = [this.title?.name, this.firstName, this.lastName].filter((part) => part != null && part !== 'null');
    return parts.join(' ').trim();
  }

  public getInitials(): string {
    const first = (this.firstName || '').charAt(0) || '';
    const last = (this.lastName || '').charAt(0) || '';
    return first + last || '?';
  }

  public isPartnerAdmin(): boolean {
    return !!this.partnerAdmin?.uid;
  }

  public isDDUser(): boolean {
    return this.hasDDRole('admin') || this.hasDDRole('analyst');
  }

  public isDDAdmin(): boolean {
    return this.hasDDRole('admin');
  }

  public hasDDRole(role: 'admin' | 'analyst'): boolean {
    const isDueDiligencePartner = this.ddPartner?.isDueDiligence === true;

    if (role === 'admin') {
      return isDueDiligencePartner && this.isPartnerAdmin();
    }

    if (role === 'analyst') {
      return isDueDiligencePartner && !!this.isAnalystUser;
    }

    return false;
  }
}
